import { useTranslator } from "@sunrise/translator";
import { Nullable } from "@sunrise/utils";
import { useReadableEpgSchedulingInfo } from "@sunrise/yallo-epg";
import clsx from "clsx";
import { ReactNode, useMemo } from "react";

import { ChannelLogo } from "@/components/channel-logo/channel-logo";
import { TimeInfo } from "@/modules/details/details.types";
import { typography } from "@/styles/typography.css";

import { AssetId } from "@sunrise/backend-types-core";
import { DetailsCastDirector } from "../details-cast-director";
import * as styles from "./details-info.css";

export type DetailsInfoProps = {
  title: Nullable<string>;
  subtitle?: Nullable<string>;
  description: Nullable<string>;
  channelLogo?: Nullable<string>;
  time?: Nullable<TimeInfo>;
  assetId: AssetId;
  showCastDirector?: boolean;
} & CommonProps;

export function DetailsInfo({
  title,
  time,
  channelLogo,
  description,
  subtitle,
  assetId,
  showCastDirector,
  "data-testid": testId,
}: DetailsInfoProps): ReactNode {
  const t = useTranslator();

  const timings = useMemo(
    () => (time ? { startTime: time.start, endTime: time.end } : null),
    [time],
  );
  const when = useReadableEpgSchedulingInfo(
    timings,
    t,
    time ? time.now : undefined,
  );
  const hasProgrammation = !!channelLogo || !!when.status;

  return (
    <>
      {!title ? null : (
        <h1
          data-testid={`${testId}.title`}
          className={clsx(styles.title, typography.h1.bold)}
        >
          {title}
        </h1>
      )}
      {!subtitle ? null : (
        <h2
          data-testid={`${testId}.subtitle`}
          className={clsx(styles.subtitle, typography.h6.bold)}
        >
          {subtitle}
        </h2>
      )}
      {!description ? null : (
        <p
          data-testid={`${testId}.description`}
          className={clsx(typography.h6.regular, styles.description)}
        >
          {description}
        </p>
      )}
      {showCastDirector ? <DetailsCastDirector assetId={assetId} /> : null}
      {hasProgrammation ? (
        <div className={styles.programmation}>
          {channelLogo ? (
            <ChannelLogo
              className={styles.channelLogo}
              url={channelLogo}
              data-testid="channel-logo"
            />
          ) : null}
          {when.status ? (
            <div data-testid="timing" className={typography.h6.bold}>
              {[when.status, when.schedule].filter(Boolean).join(", ")}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
